import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  getNodesBounds,
  type Node,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { Search, X } from 'lucide-react'
import { OrgNodeCard, accentFor, type OrgNodeData } from './OrgNodeCard'
import {
  selectVisible,
  forestLayout,
  groupedLayout,
  indexTree,
  descendantIds,
  type LayoutDir,
  type GroupBy,
} from './orgChartLayout'
import { exportPng, exportPdf } from './exportOrgChart'
import type { OrgChartNode, DottedLineEdge } from '@/types/hr'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { EmployeeForm } from '@/components/hr/EmployeeForm'
import { useToast } from '@/hooks/use-toast'
import { useUpdateEmployee, useDeleteEmployee, useEmployee, useHrContext } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'

function GroupLabelNode({ data }: { data: { label: string } }) {
  return (
    <div className="rounded bg-muted px-2 py-1 text-xs font-semibold text-foreground">
      {data.label}
    </div>
  )
}

interface ContextMenuState {
  id: string
  x: number
  y: number
}
interface PendingMove {
  sourceId: string
  sourceName: string
  targetId: string | null
  targetName: string
}

export function OrgChartDiagram({
  roots,
  dottedLines = [],
}: {
  roots: OrgChartNode[]
  dottedLines?: DottedLineEdge[]
}) {
  const { toast } = useToast()
  const { data: hrCtx } = useHrContext()
  const isHrAdmin = hrCtx?.isHrAdmin ?? false
  const updateEmployee = useUpdateEmployee()
  const deleteEmployee = useDeleteEmployee()
  const { getIntersectingNodes, fitView, setViewport } = useReactFlow()
  const canvasRef = useRef<HTMLDivElement>(null)

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())
  const [focusId, setFocusId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [dir, setDir] = useState<LayoutDir>('TB')
  const [groupBy, setGroupBy] = useState<GroupBy>('none')
  const [menu, setMenu] = useState<ContextMenuState | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [editId, setEditId] = useState<string | null>(null)
  const [addOpen, setAddOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const { byId } = useMemo(() => indexTree(roots), [roots])
  const editQuery = useEmployee(editId ?? '')

  const onToggle = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const onOpenMenu = useCallback((id: string, x: number, y: number) => {
    setMenu({ id, x, y })
  }, [])

  const model = useMemo(() => {
    const {
      nodes: visNodes,
      edges: visEdges,
      collapsed,
    } = selectVisible(roots, {
      collapsedIds,
      focusId,
    })
    const term = searchTerm.trim().toLowerCase()
    const grouped = groupBy !== 'none'

    let positions: Map<string, { x: number; y: number }>
    const groupNodes: Node[] = []
    if (grouped) {
      const { pos, groups } = groupedLayout(visNodes, groupBy as 'department' | 'country')
      positions = pos
      groups.forEach((g) => {
        groupNodes.push({
          id: `group-${g.key}`,
          type: 'groupLabel',
          position: { x: g.x, y: g.y },
          data: { label: g.label },
          draggable: false,
          selectable: false,
        })
      })
    } else {
      // Hierarchy: each root tree as its own block, stacked vertically
      // (Paul Walker's tree above Jeff Mills's), orphans gridded beneath.
      positions = forestLayout(visNodes, visEdges, dir)
    }

    const draggable = !grouped && !focusId && isHrAdmin
    const rfNodes: Node[] = visNodes.map((n) => {
      const dr = n.children?.length ?? 0
      const name = `${n.preferred_name || n.first_name} ${n.last_name}`.toLowerCase()
      const highlighted = term.length > 0 && name.includes(term)
      const data: OrgNodeData = {
        node: n,
        directReports: dr,
        hasChildren: dr > 0,
        collapsed: collapsed.has(n.id),
        highlighted,
        dimmed: term.length > 0 && !highlighted,
        dir,
        accent: accentFor(n.department_name),
        onToggle,
        onOpenMenu,
      }
      return {
        id: n.id,
        type: 'orgNode',
        position: positions.get(n.id) ?? { x: 0, y: 0 },
        data,
        draggable,
      }
    })
    const rfEdges: Edge[] = grouped
      ? []
      : visEdges.map((e) => ({
          id: `${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          type: 'smoothstep',
        }))

    // Dotted-line (secondary) reports: dashed purple edges, shown in the
    // hierarchy view when both people are visible.
    if (!grouped) {
      const visibleIds = new Set(visNodes.map((n) => n.id))
      for (const dl of dottedLines) {
        if (visibleIds.has(dl.employee_id) && visibleIds.has(dl.manager_id)) {
          rfEdges.push({
            id: `dotted-${dl.id}`,
            source: dl.manager_id,
            target: dl.employee_id,
            // Bezier curve drawn ABOVE the cards (zIndex): a dotted/secondary line
            // is usually cross-tree, so an orthogonal route gets hidden behind
            // intervening cards and looks like it connects to the wrong person.
            type: 'bezier',
            zIndex: 1000,
            label: dl.note || undefined,
            style: { stroke: '#941052', strokeWidth: 2, strokeDasharray: '6 4' },
            labelStyle: { fontSize: 10, fill: '#941052' },
            labelBgStyle: { fill: '#ffffff' },
            labelBgPadding: [4, 2],
          })
        }
      }
    }
    return { rfNodes: [...groupNodes, ...rfNodes], rfEdges }
  }, [
    roots,
    dottedLines,
    collapsedIds,
    focusId,
    dir,
    groupBy,
    searchTerm,
    onToggle,
    onOpenMenu,
    isHrAdmin,
  ])

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  useEffect(() => {
    setNodes(model.rfNodes)
    setEdges(model.rfEdges)
  }, [model, setNodes, setEdges])

  // Open with the whole chart fit to the canvas (same as the "Fit" button), so
  // the full org is visible at a glance; the user can then zoom / pan / search.
  const frameView = useCallback(() => {
    requestAnimationFrame(() => {
      fitView({ maxZoom: 1, padding: 0.1, duration: 0 })
    })
  }, [fitView])

  const didInitFit = useRef(false)
  useEffect(() => {
    if (didInitFit.current || nodes.length === 0) return
    didInitFit.current = true
    frameView()
  }, [nodes, frameView])

  // Re-frame when the layout mode changes (e.g. switching to a grouped view).
  useEffect(() => {
    if (didInitFit.current) frameView()
  }, [groupBy, dir, frameView])

  // Fit to the full width of the canvas (top-aligned), so the chart fills the
  // page horizontally and scrolls vertically — handy for wide org charts.
  const fitWidth = useCallback(() => {
    if (nodes.length === 0 || !canvasRef.current) return
    const bounds = getNodesBounds(nodes)
    const pad = 40
    const w = canvasRef.current.clientWidth
    if (!w || !bounds.width) return
    const zoom = Math.min(2, Math.max(0.1, (w - pad * 2) / bounds.width))
    setViewport({ x: pad - bounds.x * zoom, y: pad - bounds.y * zoom, zoom }, { duration: 300 })
  }, [nodes, setViewport])

  const nodeTypes = useMemo(() => ({ orgNode: OrgNodeCard, groupLabel: GroupLabelNode }), [])

  const onNodeDragStop = useCallback(
    (_evt: MouseEvent | TouchEvent, node: Node) => {
      if (groupBy !== 'none' || focusId) return
      const dragged = byId.get(node.id)
      if (!dragged) return
      const desc = descendantIds(dragged)
      const hit = getIntersectingNodes(node).find(
        (n) => n.id !== node.id && n.type === 'orgNode' && !desc.has(n.id)
      )
      if (hit) {
        if ((dragged.manager_id ?? null) === hit.id) {
          setNodes(model.rfNodes) // already reports there — snap back
          return
        }
        const target = byId.get(hit.id)
        setPendingMove({
          sourceId: dragged.id,
          sourceName: `${dragged.first_name} ${dragged.last_name}`,
          targetId: hit.id,
          targetName: target ? `${target.first_name} ${target.last_name}` : '',
        })
      } else {
        setNodes(model.rfNodes) // dropped on empty space — snap back
      }
    },
    [byId, getIntersectingNodes, groupBy, focusId, model.rfNodes, setNodes]
  )

  const applyManager = async (sourceId: string, sourceName: string, managerId: string | null) => {
    try {
      await updateEmployee.mutateAsync({ id: sourceId, data: { manager_id: managerId } })
      toast({
        title: managerId ? 'Manager updated' : 'Moved to top level',
        description: managerId
          ? `${sourceName} now reports to ${pendingMove?.targetName ?? 'a new manager'}.`
          : `${sourceName} no longer has a manager.`,
      })
    } catch (err: any) {
      toast({
        title: 'Could not update',
        description: explainError(err, 'Update failed.'),
        variant: 'destructive',
      })
      setNodes(model.rfNodes)
    }
  }

  const menuNode = menu ? byId.get(menu.id) : null
  const menuName = menuNode ? `${menuNode.first_name} ${menuNode.last_name}` : ''

  const doExport = async (fmt: 'png' | 'pdf') => {
    setExporting(true)
    try {
      if (fmt === 'png') await exportPng(nodes)
      else await exportPdf(nodes)
    } catch {
      toast({
        title: 'Export failed',
        description: 'Could not render the chart.',
        variant: 'destructive',
      })
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name…"
            aria-label="Search the org chart by name"
            className="h-9 w-56 rounded-md border border-input bg-background pl-8 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <label className="text-sm text-muted-foreground">
          <span className="sr-only">Layout direction</span>
          <select
            value={dir}
            onChange={(e) => setDir(e.target.value as LayoutDir)}
            disabled={groupBy !== 'none'}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
            aria-label="Layout direction"
          >
            <option value="TB">Top-down</option>
            <option value="LR">Left-right</option>
          </select>
        </label>

        <label className="text-sm text-muted-foreground">
          <span className="sr-only">Group by</span>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            aria-label="Group by"
          >
            <option value="none">Hierarchy</option>
            <option value="department">Group by department</option>
            <option value="country">Group by country</option>
          </select>
        </label>

        <Button variant="outline" size="sm" onClick={() => setCollapsedIds(new Set())}>
          Expand all
        </Button>
        <Button variant="outline" size="sm" onClick={() => fitView({ duration: 300 })}>
          Fit
        </Button>
        <Button variant="outline" size="sm" onClick={fitWidth}>
          Fit width
        </Button>
        {isHrAdmin && (
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            Add person
          </Button>
        )}
        <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport('png')}>
          Export PNG
        </Button>
        <Button variant="outline" size="sm" disabled={exporting} onClick={() => doExport('pdf')}>
          Export PDF
        </Button>

        {focusId && (
          <Button variant="secondary" size="sm" onClick={() => setFocusId(null)}>
            <X className="mr-1 h-4 w-4" aria-hidden="true" />
            Exit focus
          </Button>
        )}
      </div>

      <div
        ref={canvasRef}
        className="h-[calc(100vh-13rem)] w-full rounded-lg border border-border"
        role="application"
        aria-label="Org chart diagram"
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          onNodeDragStop={onNodeDragStop}
          onNodeDoubleClick={(_e, n) => n.type === 'orgNode' && setFocusId(n.id)}
          onPaneClick={() => setMenu(null)}
          minZoom={0.1}
          maxZoom={2}
          nodesConnectable={false}
          deleteKeyCode={null}
        >
          <Background />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </div>

      {/* Right-click context menu */}
      {menu && menuNode && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenu(null)} aria-hidden="true" />
          <div
            role="menu"
            aria-label={`Actions for ${menuName}`}
            className="fixed z-50 min-w-44 rounded-md border border-border bg-popover p-1 text-sm shadow-md"
            style={{ left: menu.x, top: menu.y }}
            onKeyDown={(e) => e.key === 'Escape' && setMenu(null)}
          >
            {[
              { label: 'Focus on team', run: () => setFocusId(menu.id) },
              // Editing the org structure is HR-admin only.
              ...(isHrAdmin
                ? [
                    { label: 'Edit…', run: () => setEditId(menu.id) },
                    { label: 'Add person', run: () => setAddOpen(true) },
                    ...(menuNode.manager_id
                      ? [
                          {
                            label: 'Make top-level',
                            run: () => applyManager(menu.id, menuName, null),
                          },
                        ]
                      : []),
                    { label: 'Delete…', run: () => setDeleteId(menu.id), danger: true },
                  ]
                : []),
            ].map((item) => (
              <button
                key={item.label}
                role="menuitem"
                className={`block w-full rounded px-2 py-1.5 text-left hover:bg-muted focus:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
                  item.danger ? 'text-destructive' : ''
                }`}
                onClick={() => {
                  item.run()
                  setMenu(null)
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Reparent confirmation */}
      <AlertDialog open={!!pendingMove} onOpenChange={(o) => !o && setPendingMove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change reporting line?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingMove &&
                `Move ${pendingMove.sourceName} to report to ${pendingMove.targetName}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setNodes(model.rfNodes)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingMove)
                  applyManager(pendingMove.sourceId, pendingMove.sourceName, pendingMove.targetId)
                setPendingMove(null)
              }}
            >
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete employee?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes them from the org chart. Their direct reports will move up to their
              manager. This can be undone by re-adding or re-importing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const id = deleteId
                setDeleteId(null)
                if (!id) return
                try {
                  await deleteEmployee.mutateAsync(id)
                  toast({ title: 'Employee deleted' })
                } catch (err: any) {
                  toast({
                    title: 'Could not delete',
                    description: explainError(err, 'Delete failed.'),
                    variant: 'destructive',
                  })
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit employee forms (reused from the HR module) */}
      <EmployeeForm open={addOpen} onOpenChange={setAddOpen} />
      <EmployeeForm
        open={!!editId && !!editQuery.data?.data}
        onOpenChange={(o) => !o && setEditId(null)}
        employee={editQuery.data?.data ?? null}
      />
    </div>
  )
}
